<template>
  <div>
    <v-data-table v-model="table.selected" :headers="table.headers" :server-items-length="getTotalItems()"
      :items="tasks ? tasks.data : []" :items-per-page="tasks && tasks.meta ? parseInt(tasks.meta.per_page) : 0"
      :page.sync="table.page" :options.sync="table.options" :sort-by.sync="table.sortBy" :sort-desc.sync="table.sortDesc"
      :loading="table.loading" @click:row="rowClick" hide-default-footer item-key="name" class="elevation-1 mt-4">
      <template v-slot:top>
        <v-row no-gutters class="px-4 py-0 pt-4">
          <v-col class="py-0 pr-2">
            <v-select :items="allStatus" v-model="table.filters.status" item-text="text" item-value="id" menu-props="auto"
              label="Status" hide-details color="primary" dense outlined clearable></v-select>
          </v-col>
          <v-col class="py-0 pr-2">
            <v-select :items="types.data" v-model="table.filters.type" item-text="name" item-value="id" menu-props="auto"
              label="Tipo" hide-details color="primary" dense outlined clearable></v-select>
          </v-col>
          <v-col class="py-0 pr-2">
            <date-picker clearable label="De" noIcon outlined dense v-on:change="changeSchedulingDateStart" />
          </v-col>
          <v-col class="py-0">
            <date-picker clearable label="Até" noIcon outlined dense v-on:change="changeSchedulingDateEnd" />
          </v-col>
        </v-row>
        <v-row no-gutters class="px-4 py-0 pb-2">
          <v-col class="pr-4 py-0" cols="12" sm="12" md="12" xs="12">
            <v-btn small elevation="2" color="primary" v-on:click="fetchTasks">Aplicar</v-btn>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.bulk="{ item }">
        {{ item.bulk ? 'Sim' : 'Não' }}
      </template>

      <template v-slot:item.status="{ item }">
        <v-btn class="mr-4" text icon :color="getStatusColor(item.status)">
          <v-progress-circular v-if="item.status === STATUS.STATUS_RUNNING && item.has_started !== false" :size="25" :width="3" indeterminate
            color="primary"></v-progress-circular>

          <v-icon v-else-if="item.status === STATUS.STATUS_RUNNING">mdi-tray-full</v-icon>

          <v-icon v-else>
            {{ getStatusIcon(item.status) }}
          </v-icon>
        </v-btn>
        {{ item.display_name }}
      </template>

      <!-- RUNNING mas ainda sem log_tasks = despachada porém enfileirada (worker único) → "Na fila".
           has_started vem do backend (TaskService::index / EXISTS em log_tasks). -->
      <template v-slot:item.status_formatted="{ item }">
        {{ item.status === STATUS.STATUS_RUNNING && item.has_started === false ? 'Na fila' : item.status_formatted }}
      </template>

      <template v-slot:item.items_summary="{ item }">
        <!-- Só mostra o resumo quando a task TERMINOU (OK/ERRO). Aguardando/Executando ficam
             vazios: os counts ainda não são reais (success = total - erros; no meio da execução
             erros=0 → apareceria falso "tudo válido", ex.: 1450/1450 ✓). -->
        <template v-if="item.status === STATUS.STATUS_OK || item.status === STATUS.STATUS_ERROR">
          <template v-if="item.total_items != null">
            {{ item.success_count }}/{{ item.total_items }} <span class="success--text">✓</span><span v-if="item.error_count"> · {{ item.error_count }} <span class="error--text">✗</span></span>
          </template>
          <span v-else-if="item.error_count" class="error--text">{{ item.error_count }} ✗</span>
          <span v-else class="text--secondary">—</span>
        </template>
      </template>

      <template v-slot:footer>
        <v-pagination class="mb-4" v-model="table.page" :total-visible="7"
          :length="tasks && tasks.meta ? Math.ceil(parseInt(tasks.meta.total) / parseInt(tasks.meta.per_page)) : 0"></v-pagination>
      </template>
    </v-data-table>

    <v-snackbar v-model="snackbar.show" timeout="3000" :color="snackbar.color">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Ok
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { handleResponseError } from '@/api/handleError'
import DatePicker from '@/components/DatePicker'
import { STATUS_ERROR, STATUS_OK, STATUS_RUNNING, STATUS_WAITING } from '@/constants/tasks'

export default {
  name: 'Tasks',
  computed: {
    ...mapState({
      tasks: state => state.tasks.all,
      types: state => state.tasks.types,
    }),
    STATUS() {
      return {
        STATUS_ERROR,
        STATUS_OK,
        STATUS_RUNNING,
        STATUS_WAITING
      }
    }
  },
  components: {
    DatePicker
  },
  methods: {
    fetchTasks: async function () {
      this.table.loading = true
      await this.$store.dispatch('tasks/getTasks', {
        date_start: this.table.filters.dateStart,
        date_end: this.table.filters.dateEnd,
        status: this.table.filters.status,
        type: this.table.filters.type,
        limit: this.limit,
        page: this.page,
        sort_direction: this.sortDesc ? 'desc' : 'asc',
        sort: this.sortBy
      })
        .catch(e => {
          const message = handleResponseError(e, 'Ocorreu um erro ao buscar as tarefas.')
          this.showNotification(message, 'red darken-2')
          this.table.loading = false
        })
      this.table.loading = false
    },
    fetchTypes: async function () {
      this.loadingTypes = true
      await this.$store.dispatch('tasks/getTypes').catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar os tipos de tarefas.')
        this.showNotification(message, 'red darken-2')
        this.loadingTypes = false
      })
      this.loadingTypes = false
    },
    changeSchedulingDateStart(date) {
      this.table.filters.dateStart = date
    },
    changeSchedulingDateEnd(date) {
      this.table.filters.dateEnd = date
    },
    getColorByStatus(status) {
      switch (status) {
        case 'WAITING':
          return 'blue'
        case 'ERROR':
          return 'red'
        case 'OK':
          return 'green'
        case 'RUNNING':
          return 'cyan'
      }
    },
    showNotification: function (message, color) {
      this.snackbar.text = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
    rowClick: function (task) {
      this.$router.push('/task/' + task.id)
    },
    getTotalItems: function () {
      return this.tasks && this.tasks.meta ? parseInt(this.tasks.meta.total) : 0
    },
    getStatusColor: function (status) {
      switch (status) {
        case STATUS_OK:
          return 'success';
        case STATUS_ERROR:
          return 'error';
        case STATUS_RUNNING:
          return 'info';
        case STATUS_WAITING:
          return 'warning';
        default:
          return 'info';
      }
    },
    getStatusIcon: function (status) {
      switch (status) {
        case STATUS_OK:
          return 'mdi-check-circle';
        case STATUS_ERROR:
          return 'mdi-close-circle';
        case STATUS_WAITING:
          return 'mdi-minus-circle';
        default:
          return 'mdi-minus-circle';
      }
    }
  },
  watch: {
    'table.options': function (options) {
      let sortBy = options.sortBy[0]
      const sortDesc = options.sortDesc[0]
      if ((sortBy || '').includes('formatted')) {
        const s = sortBy.split('_formatted')
        sortBy = s[0]
      }

      this.sortBy = sortBy
      this.sortDesc = sortDesc
      this.page = options.page
      this.limit = 25
      this.fetchTasks()
    }
  },
  created() {
    this.fetchTasks()
    this.fetchTypes()
  },
  data: () => ({
    firebaseStatusList: {},
    loadingTypes: false,
    // Alinhado ao table.sortBy ('id'): sem isto, o created() faz 1 fetch por 'date' e o
    // watch de options faz outro por 'id', e a lista "pisca" (reordena) ao montar.
    sortBy: 'id',
    sortDesc: true,
    limit: 25,
    page: 1,
    snackbar: {
      text: '',
      show: false,
      color: 'info',
    },
    table: {
      loading: false,
      filters: {
        selectValue: null,
        selectOptions: [],
        dateStart: '',
        dateEnd: '',
        status: '',
        type: '',
      },
      headers: [
        { text: '', value: 'status', align: 'start', sortable: false },
        { text: 'Id', value: 'id', align: 'start' },
        { text: 'Data', value: 'date_formatted' },
        { text: 'Tipo', value: 'type_formatted' },
        { text: 'Status', value: 'status_formatted' },
        { text: 'Itens', value: 'items_summary', align: 'start', sortable: false },
        { text: 'Agendado em', value: 'created_at_formatted' },
        { text: 'Agendado por', value: 'user.name', sortable: false },
      ],
      page: 1,
      sortBy: 'id',
      sortDesc: true,
      options: {},
    },
    allStatus: [
      { id: 'WAITING', text: 'Aguardando' },
      { id: 'ERROR', text: 'Com erro' },
      { id: 'OK', text: 'Concluído' },
      { id: 'RUNNING', text: 'Executando' },
    ]
  }),
}
</script>
